"use client";
import { usePathname, useSearchParams } from "next/navigation";
import {
  ReactNode,
  createContext,
  use,
  useContext,
  useEffect,
  useState,
} from "react";
// eslint-disable-next-line import/no-unresolved
import { generators } from "@/components/shared/generators/generators";
import { getModel } from "@/lib/api/generate";
// import { create } from "domain";
import { getMasterTag, submitTagApplication } from "@/lib/api/tag";
// import { set } from "react-hook-form";

interface TagGroup {
  _id: string;
  names: { code: string; value: string }[];
}
// for ugc tags and create page
interface UgcContext {
  fetchStyleUgc: (name: string, initialGroups: string[]) => Promise<void>;
  selectedStyleUgc: {
    status: string; // todo: react query for all our async state and infinite scroll stuff
    name: string;
    tagGroups: any[];
  };
  setSelectedStyleUgc: React.Dispatch<
    React.SetStateAction<{
      status: string;
      name: string;
      tagGroups: TagGroup[];
    }>
  >;
  currGroups: string[];
  selectedStyleGroupsUgc: string[];
  setSelectedStyleGroupsUgc: React.Dispatch<React.SetStateAction<string[]>>;
  currMaster: string;
  setMaster: React.Dispatch<React.SetStateAction<string>>;
  currStyle: string;
  setCurrStyle: React.Dispatch<React.SetStateAction<string>>;
  selectedStyleCreateUgc: {
    name: string;
    _id: string;
    tagGroups: any[];
  };
  setSelectedStyleCreateUgc: React.Dispatch<React.SetStateAction<any>>;
  selectedStyleGroupCreateUgc: any;
  setSelectedStyleGroupCreateUgc: React.Dispatch<React.SetStateAction<any>>;
  positivePromptCreateUgc: string;
  setPositivePromptCreateUgc: React.Dispatch<React.SetStateAction<string>>;
  nameCreateUgc: string;
  setNameCreateUgc: React.Dispatch<React.SetStateAction<string>>;
  createUgcStatus: { status: string; errorMessage: string };
  setCreateUgcStatus: React.Dispatch<React.SetStateAction<any>>;
  fetchStyleCreateUgc: (name: string, initial: boolean) => Promise<void>;
  createTagPageGroupOpen: number;
  setCreateTagPageGroupOpen: React.Dispatch<React.SetStateAction<number>>;
  createTagPageStyleOpen: number;
  setCreateTagPageStyleOpen: React.Dispatch<React.SetStateAction<number>>;
  createTagPageDetailsOpen: number;
  setCreateTagPageDetailsOpen: React.Dispatch<React.SetStateAction<number>>;
  selectedMasterUgc: {
    id: string;
    style: string;
    tag: { names?: any };
  };
  setSelectedMasterUgc: React.Dispatch<React.SetStateAction<any>>;
  fetchVariantUgc: (name: string, masterTag: string) => Promise<void>;
}

const defaultContext: UgcContext = {
  fetchStyleUgc: async () => {},
  selectedStyleUgc: { status: "loading", name: "", tagGroups: [] },
  setSelectedStyleUgc: () => {},
  selectedStyleGroupsUgc: [],
  setSelectedStyleGroupsUgc: () => {},
  currGroups: [],
  currMaster: "",
  setMaster: () => {},
  currStyle: "",
  setCurrStyle: () => {},
  selectedStyleCreateUgc: { name: "", _id: "", tagGroups: [] },
  setSelectedStyleCreateUgc: () => {},
  selectedStyleGroupCreateUgc: { _id: "", names: [] },
  setSelectedStyleGroupCreateUgc: () => {},
  positivePromptCreateUgc: "",
  setPositivePromptCreateUgc: () => {},
  nameCreateUgc: "",
  setNameCreateUgc: () => {},
  createUgcStatus: { status: "default", errorMessage: "" },
  setCreateUgcStatus: () => {},
  fetchStyleCreateUgc: async () => {},
  createTagPageGroupOpen: 0,
  setCreateTagPageGroupOpen: () => {},
  createTagPageStyleOpen: 1,
  setCreateTagPageStyleOpen: () => {},
  createTagPageDetailsOpen: 0,
  setCreateTagPageDetailsOpen: () => {},
  selectedMasterUgc: { id: "", style: "", tag: {} },
  setSelectedMasterUgc: () => {},
  fetchVariantUgc: async () => {},
};

const UgcContext = createContext<UgcContext>(defaultContext);

export default function useUgc() {
  return useContext(UgcContext);
}

export type Props = {
  children: ReactNode;
};

export function UgcContextProvider({ children }: Props) {
  const defaultStyle = generators[0].id;

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [currTab, setCurrTab] = useState(
    searchParams.get("filter") || "recent",
  );
  const [currStyle, setCurrStyle] = useState(
    searchParams.get("style") || defaultStyle,
  );
  const [currMaster, setMaster] = useState(searchParams.get("master") || "");
  const [currGroups, setCurrGroups] = useState([]);
  const [selectedStyleUgc, setSelectedStyleUgc] = useState<{
    status: string;
    name: string;
    tagGroups: TagGroup[];
  }>({
    status: "loading",
    name: "",
    tagGroups: [],
  });

  const [selectedStyleGroupsUgc, setSelectedStyleGroupsUgc] = useState<
    string[]
  >((searchParams.get("group")?.split(",") || []) as string[]);

  const [hiddenTags, setHiddenTags] = useState<{
    status: string;
    tags: string[];
  }>({ status: "default", tags: [] });
  const [savedTags, setSavedTags] = useState<{
    status: string;
    tags: string[];
  }>({ status: "default", tags: [] });

  const fetchStyleUgc = async (name: string, initialGroups: string[]) => {
    if (selectedStyleUgc.name != name) {
      setSelectedStyleUgc({ status: "loading", name, tagGroups: [] });
      const result = await getModel(name);
      if (result.error) {
        setSelectedStyleUgc({ status: "error", name, tagGroups: [] });
      } else {
        let response: any = result.response;

        const allGroup: TagGroup = {
          _id: "all",
          names: [
            {
              code: "en-US",
              value: "All",
            },
            {
              code: "ja-JP",
              value: "すべて",
            },
            {
              code: "ko-KR",
              value: "모두",
            },
            {
              code: "zh-CN",
              value: "全部",
            },
            {
              code: "zh-TW",
              value: "全部",
            },
            {
              code: "es-MX",
              value: "Todos",
            },
            {
              code: "de-DE",
              value: "Alle",
            },
            {
              code: "fr-CA",
              value: "Tous",
            },
            {
              code: "ru-RU",
              value: "Все",
            },
            {
              code: "pt-BR",
              value: "Todos",
            },
          ],
        };

        const tagGroups: TagGroup[] = [allGroup, ...response.data.tagGroups];
        const validGroupIds = tagGroups.map((group) => group._id);

        // filter the initialGroups to only include valid group IDs
        let selectedGroups = initialGroups
          .filter((id) => validGroupIds.includes(id))
          .sort();

        // if no valid groups are selected, default to "all"
        if (selectedGroups.length === 0) {
          selectedGroups = ["all"];
        }

        // compare the current groups in the URL with the valid selected groups
        const currentGroupsInUrl = initialGroups.join(",");
        const validGroupsInUrl = selectedGroups.join(",");

        // Rewrite the URL if the groups don't match
        if (currentGroupsInUrl !== validGroupsInUrl) {
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.set("group", validGroupsInUrl);
          // create a new query string with all existing params plus the updated group param
          const newUrl = `${
            window.location.pathname
          }?${searchParams.toString()}`;
          window.history.replaceState(null, "", newUrl);
        }
        setSelectedStyleUgc({ status: "loaded", name, tagGroups });
        setSelectedStyleGroupsUgc(selectedGroups);
        setCurrStyle(name);

        // TODO: oak handle selected groups as expected on tag page
        // dispatch({
        //   type: "SET_SELECTED_STYLE_GROUPS_UGC",
        //   payload: selectedGroups,
        // });
      }
    }
  };

  // Create ugc stuff
  const [selectedStyleCreateUgc, setSelectedStyleCreateUgc] = useState<{
    name: string;
    _id: string;
    tagGroups: any[];
  }>({ name: "", _id: "", tagGroups: [] });

  const [selectedStyleGroupCreateUgc, setSelectedStyleGroupCreateUgc] =
    useState<any>({ _id: "", names: [] });

  const [positivePromptCreateUgc, setPositivePromptCreateUgc] = useState("");

  const [nameCreateUgc, setNameCreateUgc] = useState("");

  const [createUgcStatus, setCreateUgcStatus] = useState<{
    status: string;
    errorMessage: string;
  }>({ status: "default", errorMessage: "" });

  const fetchStyleCreateUgc = async (
    name: string,
    initial: boolean = false,
  ) => {
    if (!initial) {
      // setCreateTagPageGroupOpen(1);
      // setCreateTagPageStyleOpen(0);
    }

    if (selectedStyleCreateUgc.name != name) {
      setSelectedStyleCreateUgc({ name, _id: "", tagGroups: [] });
      const result = await getModel(name);
      if (result.error) {
        // handle failure
      } else {
        let response: any = result.response;

        const tagGroups = [...response.data.tagGroups];

        setSelectedStyleCreateUgc({ name, _id: response.data._id, tagGroups });
      }
    }
  };
  const [createTagPageGroupOpen, setCreateTagPageGroupOpen] = useState(0);
  const [createTagPageStyleOpen, setCreateTagPageStyleOpen] = useState(1);
  const [createTagPageDetailsOpen, setCreateTagPageDetailsOpen] = useState(0);

  const [selectedMasterUgc, setSelectedMasterUgc] = useState({
    id: "",
    style: "",
    tag: {},
  });

  const fetchVariantUgc = async (name: string, masterTag: string) => {
    setSelectedMasterUgc({ id: "", style: "", tag: {} });
    if (selectedMasterUgc.id != masterTag || selectedMasterUgc.style != name) {
      const result = await getMasterTag(masterTag, name);
      if (result.error) {
        // handle failure
      } else {
        let response: any = result.response;

        let data = response.data;

        setSelectedMasterUgc({
          id: masterTag,
          style: name,
          tag: { names: data.names },
        });
      }
    }
  };

  const contextValues = {
    fetchStyleUgc,
    currGroups,
    currMaster,
    currStyle,
    selectedStyleUgc,
    setSelectedStyleUgc,
    selectedStyleGroupsUgc,
    setSelectedStyleGroupsUgc,
    setMaster,
    setCurrStyle,
    selectedStyleCreateUgc,
    setSelectedStyleCreateUgc,
    selectedStyleGroupCreateUgc,
    setSelectedStyleGroupCreateUgc,
    positivePromptCreateUgc,
    setPositivePromptCreateUgc,
    nameCreateUgc,
    setNameCreateUgc,
    createUgcStatus,
    setCreateUgcStatus,
    fetchStyleCreateUgc,
    createTagPageGroupOpen,
    setCreateTagPageGroupOpen,
    createTagPageStyleOpen,
    setCreateTagPageStyleOpen,
    createTagPageDetailsOpen,
    setCreateTagPageDetailsOpen,
    selectedMasterUgc,
    setSelectedMasterUgc,
    fetchVariantUgc,
  };

  return (
    <UgcContext.Provider value={contextValues}>{children}</UgcContext.Provider>
  );
}
